/**
 * How a listing job moves (the `sync_jobs` queue). A job asks one worker to
 * publish, reprice or delist one listing on one marketplace. Jobs come from
 * the item page ('crosslist'), a person's retry or take-down ('manual'), and
 * the sale trigger ('system'), which queues a delist for every other live
 * listing when an item sells.
 *
 * A worker claims a job with FOR UPDATE SKIP LOCKED, so two paired browsers
 * never run the same one. A claim held past STALE_CLAIM_MINUTES without a
 * report (the browser closed mid-job) goes back in the queue. Failures retry
 * with backoff until max_attempts, then park the listing in 'error'.
 */
import type { JobAction } from "./registry.ts";

export const JOB_SOURCES = ["crosslist", "manual", "system"] as const;
export type JobSource = (typeof JOB_SOURCES)[number];

export const JOB_STATUSES = ["queued", "running", "succeeded", "failed", "abandoned"] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/** A claim with no report for this long is handed to the next worker. */
export const STALE_CLAIM_MINUTES = 15;

/** A paired browser that checked in within this window counts as online. */
export const DEVICE_ONLINE_MINUTES = 10;

/** How often each storefront's sold page is read, across all of a workspace's paired browsers. */
export const SALES_CHECK_MINUTES = 15;

/** How often a paired browser should check in while it is open. */
export const DEVICE_POLL_SECONDS = 60;

/** Delist first: an item that sold must come down before anything new goes up. */
export const ACTION_PRIORITY: Record<JobAction, number> = { delist: 0, update_price: 1, publish: 2 };

/** Minutes before retry n (1-based): 1, 3, 9 … */
export function retryDelayMinutes(attempt: number): number {
  return 3 ** Math.max(attempt - 1, 0);
}

export interface JobState {
  action: JobAction;
  itemStatus: string;
  itemArchived: boolean;
  /** Null when the listing row is gone. */
  listingStatus: string | null;
  externalId: string | null;
}

const WRITTEN_OFF = ["donated", "lost", "damaged"];

/**
 * Why a queued job should no longer run, or null when it should. Checked when
 * a worker claims it, because the world moves while a job waits: the item
 * sells, or the listing is removed by hand.
 */
export function staleReason(s: JobState): string | null {
  if (s.listingStatus === null) return "The listing was removed";
  if (s.action === "publish") {
    if (s.itemArchived) return "The item was archived";
    if (s.itemStatus === "sold" || s.itemStatus === "shipped") return "The item already sold";
    if (WRITTEN_OFF.includes(s.itemStatus)) return "The item was written off";
    if (s.listingStatus === "active" && s.externalId) return "Already live";
  }
  if (s.action === "update_price") {
    if (!s.externalId) return "Not live on the marketplace yet";
    if (s.listingStatus !== "active") return "The listing isn't live";
  }
  if (s.action === "delist" && !s.externalId) {
    return "There is no marketplace id for this listing, so there is nothing to take down";
  }
  return null;
}

/**
 * The action a crosslist request becomes for one marketplace: a listing that
 * is already live there is repriced, anything else is published.
 */
export function crosslistAction(listing: { status: string; externalId: string | null } | null): JobAction {
  return listing && listing.status === "active" && listing.externalId ? "update_price" : "publish";
}
